import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { MarketStore, Instance, Market, MarketCatalogue, MarketOrderType, MarketSupplier } from '../../app/services/api/orderManagementClient';
import { client } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';
import { isOrderTypeDSD } from '../../enums/OrderType';

const StoreForm = () => {
  const { t } = useTranslation('stores');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logError, extractErrorMessage } = useLogError();
  const { selectedSystem } = useContext(UserContext);

  const isEditMode = Boolean(id);
  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [formData, setFormData] = useState<MarketStore>({
    customerId: 0,
    companyId: parseInt(companyId || '0'),
    instanceName: '',
    customerTypeId: parseInt(customerTypeId || '0'),
    marketName: '',
    erpLocationId: undefined,
    storeNumber: '',
    storeName: '',
    emailAddress: '',
    phoneNumber: '',
    shipToLocation: '',
    shipToAddress: '',
    town: '',
    county: '',
    country: '',
    postCode: '',
    inventoryEnabled: false,
    inventoryTestEnabled: false,
    inventoryStartDate: '',
    isParEnabled: undefined,
    catalogueIds: [],
    odsConfiguration: [],
    suppliersAccountNumber: [],
    orderTypesAccountNumber: [],
  });

  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [catalogues, setCatalogues] = useState<MarketCatalogue[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [marketSuppliers, setMarketSuppliers] = useState<MarketSupplier[]>([]);
  
  const isOfs = selectedSystem?.name === 'Orderly for Stores';

  useEffect(() => {
    loadInstances();
    if (isEditMode && id) {
      loadStore(id);
    } else {
      setIsLoading(false);
    }
  }, [isEditMode, id]);

  useEffect(() => {
    if (formData.companyId > 0) {
      loadMarkets(formData.companyId.toString());
    }
  }, [formData.companyId]);

  useEffect(() => {
    if (formData.companyId > 0 && formData.customerTypeId > 0) {
      loadCatalogues(formData.companyId.toString());
    }
  }, [formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    if (formData.companyId > 0 && formData.customerTypeId > 0) {
      loadOrderTypes(formData.companyId.toString(), formData.customerTypeId.toString());
      loadMarketSuppliers(formData.companyId.toString(), formData.customerTypeId.toString());
    }
  }, [formData.companyId, formData.customerTypeId]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadCatalogues = async (instanceId: string) => {
    try {
      const response = await client.getMarketCatalogues({
        companyId: instanceId,
        customerTypeId: formData.customerTypeId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setCatalogues(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load catalogues');
    }
  };

  const loadOrderTypes = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketOrderTypes({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setOrderTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load order types');
    }
  };

  const loadMarketSuppliers = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketSuppliers({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarketSuppliers(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load market suppliers');
    }
  };

  const loadStore = async (storeId: string) => {
    try {
      const response = await client.getMarketStore(storeId);
      
      if (response.success && response.data) {
        setFormData({
          ...response.data,
          catalogueIds: response.data.catalogueIds || [],
          suppliersAccountNumber: response.data.suppliersAccountNumber || [],
          orderTypesAccountNumber: response.data.orderTypesAccountNumber || [],
          inventoryStartDate: response.data.inventoryStartDate,
          companyId: response.data.companyId || 0,
          customerTypeId: response.data.customerTypeId || 0,
        });
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError('Failed to load store');
      setErrorMessage(t('loadErrorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNumberInputChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: 0,
      marketName: '',
      catalogueIds: [],
      odsConfiguration: [],
      suppliersAccountNumber: [],
      orderTypesAccountNumber: [],
    }));
    
    setMarkets([]);
    setCatalogues([]);
    setOrderTypes([]);
    setMarketSuppliers([]);
    
    if (value?.id) {
      loadMarkets(value.id.toString());
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    const marketId = value?.customerTypeId || 0;
    setFormData(prev => ({
      ...prev,
      customerTypeId: marketId,
      marketName: value?.name || '',
      catalogueIds: [],
      odsConfiguration: [],
      suppliersAccountNumber: [],
      orderTypesAccountNumber: [],
    }));
    
    setCatalogues([]);
    setOrderTypes([]);
    setMarketSuppliers([]);
  };

  const handleDeliveryScheduleChange = (orderTypeId: number, deliveryDay: number, daysToCutoff: number) => {
    setFormData(prev => {
      const newOdsConfiguration = [...(prev.odsConfiguration || [])];
      const existingIndex = newOdsConfiguration.findIndex(
        ods => ods.orderTypeId === orderTypeId && ods.deliveryDay === deliveryDay,
      );

      if (existingIndex >= 0) {
        if (daysToCutoff === 0) {
          newOdsConfiguration.splice(existingIndex, 1);
        } else {
          newOdsConfiguration[existingIndex] = {
            ...newOdsConfiguration[existingIndex],
            daysToCutoff: daysToCutoff.toString(),
          };
        }
      } else if (daysToCutoff > 0) {
        newOdsConfiguration.push({
          deliveryScheduleRuleId: undefined,
          orderTypeId: orderTypeId,
          orderType: orderTypes.find(ot => ot.orderTypeId === orderTypeId)?.name || '',
          week: 0,
          deliveryDay: deliveryDay,
          daysToCutoff: daysToCutoff.toString(),
          supplierNumber: '',
          supplierName: '',
        });
      }

      return {
        ...prev,
        odsConfiguration: newOdsConfiguration,
      };
    });
  };

  const handleSupplierDeliveryScheduleChange = (
    orderTypeId: number,
    deliveryDay: number,
    daysToCutoff: number,
    supplierName?: string | null,
    supplierNumber?: string | null,
  ) => {
    setFormData(prev => {
      const newOdsConfiguration = [...(prev.odsConfiguration || [])];
      const existingIndex = newOdsConfiguration.findIndex(
        ods => ods.orderTypeId === orderTypeId && 
               ods.deliveryDay === deliveryDay &&
               (supplierNumber
                 ? ods.supplierNumber === supplierNumber
                 : !ods.supplierNumber),
      );

      if (existingIndex >= 0) {
        if (daysToCutoff === 0) {
          newOdsConfiguration.splice(existingIndex, 1);
        } else {
          newOdsConfiguration[existingIndex] = {
            ...newOdsConfiguration[existingIndex],
            daysToCutoff: daysToCutoff.toString(),
          };
        }
      } else if (daysToCutoff > 0) {
        const existingConfig = newOdsConfiguration.find(
          config => config.orderTypeId === orderTypeId &&
                    (supplierNumber
                      ? config.supplierNumber === supplierNumber
                      : !config.supplierNumber),
        );
        
        newOdsConfiguration.push({
          deliveryScheduleRuleId: undefined,
          orderTypeId: orderTypeId,
          orderType: orderTypes.find(ot => ot.orderTypeId === orderTypeId)?.name || '',
          week: existingConfig?.week || 1,
          deliveryDay: deliveryDay,
          daysToCutoff: daysToCutoff.toString(),
          supplierNumber: supplierNumber || '',
          supplierName: supplierName || '',
        });
      }

      return {
        ...prev,
        odsConfiguration: newOdsConfiguration,
      };
    });
  };

  const handleCatalogueToggle = (catalogueId: number) => {
    setFormData(prev => ({
      ...prev,
      catalogueIds: prev.catalogueIds.includes(catalogueId)
        ? prev.catalogueIds.filter(id => id !== catalogueId)
        : [...prev.catalogueIds, catalogueId],
    }));
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/stores${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    try {
      let response;
      
      if (isEditMode && id) {
        response = await client.updateMarketStore(id, formData);
      } else {
        response = await client.createMarketStore(formData);
      }

      if (response.success) {
        setShowSuccessMessage(true);
        setTimeout(() => {
          const params = getUrlParams();
          navigate(`/stores${params.toString() ? `?${params.toString()}` : ''}`);
        }, 500);
      } else if (response.message) {
        setErrorMessage(response.message);
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError(isEditMode ? 'Failed to update store' : 'Failed to create store');
      const errorMessage = extractErrorMessage(error);
      setErrorMessage(errorMessage || (isEditMode ? t('updateErrorMessage') : t('createErrorMessage')));
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderDeliveryScheduleTable = () => {
    const days = [
      { key: 1, label: t('day1'), fullName: 'Monday' },
      { key: 2, label: t('day2'), fullName: 'Tuesday' },
      { key: 3, label: t('day3'), fullName: 'Wednesday' },
      { key: 4, label: t('day4'), fullName: 'Thursday' },
      { key: 5, label: t('day5'), fullName: 'Friday' },
      { key: 6, label: t('day6'), fullName: 'Saturday' },
      { key: 7, label: t('day7'), fullName: 'Sunday' },
    ];

    const getCutoffOptions = (deliveryDay: number) => {
      const options = [{ value: 0, label: 'No Delivery' }];
      for (let i = 1; i <= 6; i++) {
        let cutoffDay = deliveryDay - i;
        if (cutoffDay <= 0) {
          cutoffDay += 7;
        }     
        const dayNames = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        options.push({
          value: i,
          label: `Cut off ${dayNames[cutoffDay]}`,
        });
      }
      return options;
    };

    // Non-DSD order types: one row per order type
    const nonDsdRows = orderTypes
      .filter(ot => !isOrderTypeDSD(ot.orderTypeId))
      .map(orderType => ({
        ...orderType,
        displayName: orderType.name,
        supplierName: '',
        supplierNumber: '',
        isSupplierSpecific: false,
      }));

    // DSD order types: match each to its specific supplier, or expand with all suppliers
    const dsdTypes = orderTypes.filter(ot => isOrderTypeDSD(ot.orderTypeId));
    const dsdSupplierRows = (() => {
      if (dsdTypes.length === 0) return [];

      // Try to match each DSD order type to a specific market supplier via cutoff.supplierName or name
      const matchedSupplierNumbers = new Set<string>();
      const specificRows: typeof nonDsdRows = [];

      for (const dsdType of dsdTypes) {
        const matchName = dsdType.cutoff?.supplierName || dsdType.name;
        const matched = matchName
          ? marketSuppliers.find(s => s.supplierName === matchName)
          : undefined;

        if (matched) {
          matchedSupplierNumbers.add(matched.supplierNumber);
          specificRows.push({
            ...dsdType,
            displayName: dsdType.name,
            supplierName: matched.supplierName,
            supplierNumber: matched.supplierNumber,
            isSupplierSpecific: true,
          });
        }
      }

      // DSD types that didn't match a specific supplier get expanded with remaining suppliers
      const unmatchedDsdTypes = dsdTypes.filter(dsdType => {
        const matchName = dsdType.cutoff?.supplierName || dsdType.name;
        return !matchName || !marketSuppliers.find(s => s.supplierName === matchName);
      });

      const genericRows: typeof nonDsdRows = [];
      for (const dsdType of unmatchedDsdTypes) {
        const supplierMap = new Map<string, { supplierName: string; supplierNumber: string }>();

        // Add remaining market suppliers not already matched 1:1
        marketSuppliers
          .filter(s => !matchedSupplierNumbers.has(s.supplierNumber))
          .forEach(s => supplierMap.set(s.supplierNumber, { supplierName: s.supplierName, supplierNumber: s.supplierNumber }));

        Array.from(supplierMap.values()).forEach(supplier => {
          genericRows.push({
            ...dsdType,
            displayName: dsdType.name,
            supplierName: supplier.supplierName,
            supplierNumber: supplier.supplierNumber,
            isSupplierSpecific: true,
          });
        });
      }

      return [...specificRows, ...genericRows];
    })();

    const displayOrderTypes = [...nonDsdRows, ...dsdSupplierRows];

    const findConfiguration = (orderTypeId: number, deliveryDay: number, supplierName?: string | null, supplierNumber?: string | null) => {
      return (formData.odsConfiguration || []).find(
        ods => ods.orderTypeId === orderTypeId && 
               ods.deliveryDay === deliveryDay &&
               (supplierNumber
                 ? ods.supplierNumber === supplierNumber
                 : !ods.supplierNumber),
      );
    };

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('orderType')}</TableCell>
              {days.map(day => (
                <TableCell
                  key={day.key}
                  align="center"
                  sx={{ minWidth: 150 }}
                >
                  {day.fullName} Delivery
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayOrderTypes.map((orderType, index) => (
              <TableRow key={`${orderType.orderTypeId}-${orderType.supplierName || 'default'}-${index}`}>
                <TableCell>
                  {orderType.displayName}
                  {orderType.isSupplierSpecific && (
                    <Typography 
                      sx={{ fontSize: '0.75rem' }}
                      display="block" 
                      color="text.secondary"
                    >
                      {orderType.supplierName}
                    </Typography>
                  )}
                </TableCell>
                {days.map(day => {
                  const config = findConfiguration(orderType.orderTypeId, day.key, orderType.supplierName, orderType.supplierNumber);
                  const currentValue = config ? Number(config.daysToCutoff) : 0;

                  return (
                    <TableCell
                      key={day.key}
                      align="center"
                    >
                      <FormControl 
                        fullWidth 
                        size="small"
                      >
                        <Select
                          value={currentValue}
                          onChange={(event: SelectChangeEvent<number>) => {
                            const newValue = Number(event.target.value);
                            if (orderType.isSupplierSpecific) {
                              handleSupplierDeliveryScheduleChange(
                                orderType.orderTypeId, 
                                day.key, 
                                newValue,
                                orderType.supplierName,
                                orderType.supplierNumber,
                              );
                            } else {
                              handleDeliveryScheduleChange(
                                orderType.orderTypeId, 
                                day.key, 
                                newValue,
                              );
                            }
                          }}
                          displayEmpty
                        >
                          {getCutoffOptions(day.key).map(option => (
                            <MenuItem 
                              key={option.value} 
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEditMode ? 'management.editStore' : 'management.createStore')}</PageTitle>
        <Typography>{t('management.loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        {/* Basic Store Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('management.basicInformation')}
            sx={{
              color: 'text.primary',
            }}
          />
          <CardContent
            sx={{
              p: 8,
            }}
          >
            <Grid
              container
              spacing={8}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('storeName')}
                  value={formData.storeName}
                  onChange={handleInputChange('storeName')}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('storeNumber')}
                  value={formData.storeNumber}
                  onChange={handleInputChange('storeNumber')}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('emailAddress')}
                  type="email"
                  value={formData.emailAddress}
                  onChange={handleInputChange('emailAddress')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={handleInputChange('phoneNumber')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('erpLocationId')}
                  type="number"
                  value={formData.erpLocationId || ''}
                  onChange={handleNumberInputChange('erpLocationId')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Location Details Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('management.locationDetails')}
            sx={{
              color: 'text.primary',
            }}
          />
          <CardContent
            sx={{
              p: 8,
            }}
          >
            <Grid
              container
              spacing={8}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('shipToLocation')}
                  value={formData.shipToLocation}
                  onChange={handleInputChange('shipToLocation')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('shipToAddress')}
                  value={formData.shipToAddress}
                  onChange={handleInputChange('shipToAddress')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('town')}
                  value={formData.town}
                  onChange={handleInputChange('town')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('county')}
                  value={formData.county}
                  onChange={handleInputChange('county')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('country')}
                  value={formData.country}
                  onChange={handleInputChange('country')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('postCode')}
                  value={formData.postCode}
                  onChange={handleInputChange('postCode')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Instance and Market Configuration Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('configuration')}
            sx={{
              color: 'text.primary',
            }}
          />
          <CardContent
            sx={{
              p: 8,
            }}
          >
            <Grid
              container
              spacing={8}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(instance => instance.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Autocomplete
                  options={markets}
                  getOptionLabel={(option) => option.name}
                  value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                  onChange={handleMarketChange}
                  disabled={!formData.companyId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('market')}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('inventoryStartDate')}
                  type="date"
                  value={formData.inventoryStartDate || ''}
                  onChange={handleInputChange('inventoryStartDate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
              >
                <Grid
                  container
                  spacing={4}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.inventoryEnabled || false}
                          onChange={handleSwitchChange('inventoryEnabled')}
                        />
                      }
                      label={
                        <Typography
                          variant="textMD"
                          sx={{
                            ml: 1,
                          }}
                        >
                          {t('inventoryEnabled')}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: 'background.paper',
                        alignItems: 'center',
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.inventoryTestEnabled || false}
                          onChange={handleSwitchChange('inventoryTestEnabled')}
                        />
                      }
                      label={
                        <Typography
                          variant="textMD"
                          sx={{
                            ml: 1,
                          }}
                        >
                          {t('inventoryTestEnabled')}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: 'background.paper',
                        alignItems: 'center',
                      }}
                    />
                  </Grid>

                  {isOfs && (
                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isParEnabled ?? false}
                            onChange={handleSwitchChange('isParEnabled')}
                          />
                        }
                        label={
                          <Typography
                            variant="textMD"
                            sx={{
                              ml: 1,
                            }}
                          >
                            {t('isParEnabled')}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          p: 2,
                          backgroundColor: 'background.paper',
                          alignItems: 'center',
                        }}
                      />
                    </Grid>
                  )}
                 
                  {marketSuppliers?.length > 0 && <Grid item 
                    xs={12}
                    md={12}>
                    <Typography sx={{ mb: 4 }}>
                      {t('suppliersAccountNumber')}
                    </Typography>
                    <Grid container 
                      spacing={4}>
                      {marketSuppliers.map((s) => {
                        const existing = (formData.suppliersAccountNumber || []).find(acc => acc.name === s.supplierNumber);
                        return (
                          <Grid item 
                            xs={12} 
                            md={6} 
                            key={s.supplierNumber}>
                            <TextField
                              label={`${s.supplierName} - ${s.supplierNumber}`}
                              value={existing?.value ?? ''}
                              onChange={e => {
                                const value = e.target.value;
                                setFormData(prev => {
                                  const current = prev.suppliersAccountNumber || [];
                                  const i = current.findIndex(a => a.name === s.supplierNumber);
                                  if (i >= 0) {
                                    const updated = [...current];
                                    updated[i] = { ...updated[i], value };
                                    return { ...prev, suppliersAccountNumber: updated };
                                  }
                                  return { ...prev, suppliersAccountNumber: [...current, { id: 0, name: s.supplierNumber, value }] };
                                });
                              }}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                  }

                  {orderTypes?.length > 0 && <Grid item 
                    xs={12} 
                    md={12}>
                    <Typography sx={{ mb: 4 }}>
                      {t('ordertypesAccountNumber')}
                    </Typography>
                    <Grid container 
                      spacing={4}>
                      {orderTypes.map(ot => {
                        const existing = (formData.orderTypesAccountNumber || []).find(acc => acc.name === ot.orderTypeId.toString());
                        return (
                          <Grid item 
                            xs={12} 
                            md={6} 
                            key={ot.orderTypeId}>
                            <TextField
                              label={ot.name}
                              value={existing?.value ?? ''}
                              onChange={e => {
                                const value = e.target.value;
                                setFormData(prev => {
                                  const current = prev.orderTypesAccountNumber || [];
                                  const i = current.findIndex(a => a.name === ot.orderTypeId.toString());
                                  if (i >= 0) {
                                    const updated = [...current];
                                    updated[i] = { ...updated[i], value };
                                    return { ...prev, orderTypesAccountNumber: updated };
                                  }
                                  return { ...prev, orderTypesAccountNumber: [...current, { id: 0, name: ot.orderTypeId.toString(), value }] };
                                });
                              }}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                  }
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Catalogue Configuration Card */}
        {formData.companyId > 0 && formData.customerTypeId > 0 && (
          <Card elevation={1}>
            <CardHeader
              title={t('management.catalogueConfiguration')}
              sx={{
                color: 'text.primary',
              }}
            />
            <CardContent
              sx={{
                p: 8,
              }}
            >
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {t('selectCatalogues')}
              </Typography>
              <Grid 
                container 
                spacing={2}
              >
                {catalogues.map((catalogue) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={6} 
                    md={4} 
                    key={catalogue.customerGroupId}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.catalogueIds.includes(catalogue.customerGroupId)}
                          onChange={() => handleCatalogueToggle(catalogue.customerGroupId)}
                        />
                      }
                      label={catalogue.name}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Delivery Schedule Configuration Card */}
        {formData.companyId > 0 && formData.customerTypeId > 0 && orderTypes.length > 0 && (
          <Card elevation={1}>
            <CardHeader
              title={t('management.deliveryScheduleConfiguration')}
              sx={{
                color: 'text.primary',
              }}
            />
            <CardContent
              sx={{
                p: 8,
              }}
            >
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {t('orderDeliverySchedule')}
              </Typography>
              {renderDeliveryScheduleTable()}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 4,
              p: 8,
            }}
          >
            <Button
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={isSaving || !formData.storeName || !formData.storeNumber || !formData.companyId || !formData.customerTypeId}
            >
              {isSaving ? t('saving') : (isEditMode ? t('update') : t('create'))}
            </Button>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={showSuccessMessage}
        autoHideDuration={5000}
        onClose={() => setShowSuccessMessage(false)}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {isEditMode ? t('updateSuccessMessage') : t('createSuccessMessage')}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={() => setShowErrorMessage(false)}
      >
        <Alert
          onClose={() => setShowErrorMessage(false)}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default StoreForm;
